import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import Button from '../Button/Button';
import Menubar from '../Menubar/Menubar';

/**
 * `ApplicationHeaderTabs` is an application sub header container which usually appears directly under an
 * {@link ApplicationHeader}. It may contain links to tabs in your application and floating buttons.
 *
 * The container accepts [`react-router`](https://github.com/ReactTraining/react-router)'s
 * [`Link`](https://github.com/ReactTraining/react-router/blob/master/packages/react-router-dom/docs/api/Link.md)
 * component for linking to subpanels within an application. The component will automatically highlight the correct tab based
 * on the current URL.
 *
 * If your application is not using [`react-router`](https://github.com/ReactTraining/react-router), then you may add
 * HTML `a href` elements instead, but you will have to handle the navigation and setting the selected index yourself.
 *
 * @property {node} children The [`Link`](https://github.com/ReactTraining/react-router/blob/master/packages/react-router-dom/docs/api/Link.md) components
 * for each tab and floating buttons you want to add to this container. The links will automatically be grouped to the left
 * while the buttons will be grouped to the right.
 * @property {string} className The class name of this component. Will be appended to "core-application-header-tabs".
 * @property {boolean} exact If set to `true`,
 * the [`Link`](https://github.com/ReactTraining/react-router/blob/master/packages/react-router-dom/docs/api/Link.md)'s *to*
 * prop must match the entire pathname of the browser URI. Defaults to `false` meaning that a partial match at the beginning
 * of the *to* prop will result in a link being selected.
 * @property {number} selected The index of the selected tab. Should only be used when you are not
 * using [`Link`](https://github.com/ReactTraining/react-router/blob/master/packages/react-router-dom/docs/api/Link.md) elements.
 *
 * @see ApplicationHeader
 */
export default class ApplicationHeaderTabs extends PureComponent {

  static displayName = 'ApplicationHeaderTabs';

  static propTypes = {
    children: PropTypes.node,
    className: PropTypes.string,
    exact: PropTypes.bool,
    selected: PropTypes.number,
  };

  render() {
    const { children, className, exact, selected } = this.props;
    const url = window.location.pathname;
    const links = [];
    const buttons = [];
    const others = [];

    React.Children.forEach(children, child => {
      if (child && child.type) {
        if (child.type.displayName === 'Link' || child.type.name === 'Link' || child.type === 'a') {
          links.push(child);
        } else if (child.type.displayName === 'Button' || child.type.name === 'Button') {
          buttons.push(child);
        } else {
          others.push(child);
        }
      }
    });
    const sel = selected !== undefined ? selected : links.findIndex(link => {
      if (exact) {
        return url === link.props.to;
      }
      return url.indexOf(link.props.to) === 0;
    });
    return (
      <div className={cx('core-application-header-tabs', className, { 'no-links': links.length === 0 })}>
        {links.length > 0 &&
          <Menubar className="core-application-header-tabs-menubar" selected={sel} showUnderline>
            {links}
          </Menubar>}
        {buttons.length > 0 && <div className="toolbar">{buttons}</div>}
        {others.length > 0 && others}
      </div>
    );
  }

}
